import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerAuthCallback } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');

    // Validate token is non-empty string
    if (storedToken && typeof storedToken === 'string' && storedToken.trim()) {
      setToken(storedToken);
    } else {
      // Clear malformed token
      localStorage.removeItem('admin_token');
    }

    setIsLoading(false);
  }, []);

  const clearAuth = useCallback(() => {
    setToken(null);
    setMustChangePassword(false);
    localStorage.removeItem('admin_token');

    // Only redirect if not already on login page
    if (!window.location.pathname.includes('/admin/login')) {
      navigate('/admin/login');
    }
  }, [navigate]);

  // Register clearAuth callback with API interceptor
  useEffect(() => {
    registerAuthCallback(clearAuth);
  }, [clearAuth]);

  const login = (newToken, mustChange) => {
    setToken(newToken);
    setMustChangePassword(!!mustChange);
    localStorage.setItem('admin_token', newToken);
    if (mustChange) {
      navigate('/admin/change-password');
    } else {
      navigate('/admin/dashboard');
    }
  };

  const logout = () => {
    setToken(null);
    setMustChangePassword(false);
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const value = {
    token,
    isAuthenticated: !!token,
    mustChangePassword,
    isLoading,
    login,
    logout,
    clearAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
